import React from 'react';
import API from '../api';
import { containerOrder, buildInitialColumns } from '../constants';

export function useSaves({ columns, setColumns, canEdit, site }) {
  const [saveList, setSaveList] = React.useState([]);
  const [saveTitle, setSaveTitle] = React.useState('');
  const [saveColor, setSaveColor] = React.useState('red');
  const [activeSave, setActiveSave] = React.useState(null);
  const [saveNotifications, setSaveNotifications] = React.useState([]);

  const prevSavesRef = React.useRef(null);
  const selfActionRef = React.useRef(false);

  const fetchSaves = React.useCallback(async (currentSite = site) => {
    const { data } = await API.get(`/saves?site=${encodeURIComponent(currentSite)}`);
    setSaveList(data);
    return data;
  }, [site]);

  const dismissNotification = React.useCallback((id) => {
    setSaveNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  React.useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await API.get(`/saves?site=${encodeURIComponent(site)}`);

        if (selfActionRef.current) {
          selfActionRef.current = false;
          prevSavesRef.current = data;
          setSaveList(data);
          return;
        }

        const prev = prevSavesRef.current;
        if (prev) {
          const prevMap = new Map(prev.map((s) => [s.id, s]));
          const currIds = new Set(data.map((s) => s.id));

          const newNotifs = [];

          data.forEach((s) => {
            if (!prevMap.has(s.id)) {
              const disappeared = prev.find(
                (p) => p.title === s.title && !currIds.has(p.id)
              );
              if (disappeared) {
                newNotifs.push({
                  id: Date.now() + Math.random(),
                  message: `Sauvegarde "${s.title}" modifiée`,
                  by: s.updated_by || '',
                  savedAt: s.created_at ? new Date(s.created_at) : new Date()
                });
              } else {
                newNotifs.push({
                  id: Date.now() + Math.random(),
                  message: `Nouvelle sauvegarde "${s.title}"`,
                  by: s.updated_by || '',
                  savedAt: s.created_at ? new Date(s.created_at) : new Date()
                });
              }
            }
          });

          prev.forEach((p) => {
            if (!currIds.has(p.id) && !data.find((s) => s.title === p.title)) {
              newNotifs.push({
                id: Date.now() + Math.random(),
                message: `Sauvegarde "${p.title}" supprimée`,
                by: '',
                savedAt: new Date()
              });
            }
          });

          if (newNotifs.length > 0) {
            setSaveNotifications((prev) => [...prev, ...newNotifs]);
          }
        }

        prevSavesRef.current = data;
        setSaveList(data);
      } catch (err) {
        // silently ignore polling errors
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [site]);

  const initPrevSaves = React.useCallback(async (currentSite = site) => {
    try {
      const { data } = await API.get(`/saves?site=${encodeURIComponent(currentSite)}`);
      prevSavesRef.current = data;
    } catch (err) {
      // ignore
    }
  }, [site]);

  const persistActiveSaveSilently = async (nextColumns) => {
    if (!canEdit) return;
    if (!activeSave) return;

    // prépare les données comme handleSave
    const data = containerOrder.reduce((acc, key) => {
      const updatedItems = (nextColumns[key]?.items || []).map((item, i) => ({
        ...item,
        position: i,
        isTemporary: false
      }));
      acc[key] = updatedItems;
      return acc;
    }, {});

    try {
      // ✅ même logique que handleSave : on supprime l'existante (même titre + couleur) puis on recrée
      const existing = saveList.find(
        (s) =>
          s.title.toLowerCase().trim() === activeSave.title.toLowerCase().trim() &&
          (s.color || 'red') === (activeSave.color || 'red')
      );

      if (existing) {
        await API.delete(`/saves/${existing.id}`);
      }

      selfActionRef.current = true;

      await API.post('/saves', {
        title: activeSave.title,
        data,
        color: activeSave.color || 'red',
        site
      });

      await fetchSaves(site);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    if (!canEdit) {
      alert('Tu es en lecture seule (viewer ou non connecté)');
      return;
    }

    if (!saveTitle.trim()) return alert('Titre requis');

    const existing = saveList.find(
      (s) =>
        s.title.toLowerCase().trim() === saveTitle.toLowerCase().trim() &&
        (s.color || 'red') === (saveColor || 'red')
    );

    // On prépare les données à sauvegarder
    // et on enlève la pastille "non sauvegardé"
    const data = containerOrder.reduce((acc, key) => {
      const updatedItems = columns[key].items.map((item, i) => ({
        ...item,
        position: i,
        isTemporary: false
      }));
      acc[key] = updatedItems;
      return acc;
    }, {});

    if (existing) {
      const confirmOverwrite = window.confirm(
        `Une sauvegarde intitulée "${saveTitle}" avec la couleur "${saveColor}" existe déjà.\nVoulez-vous l'écraser ?`
      );
      if (!confirmOverwrite) return;
      await API.delete(`/saves/${existing.id}`);
    }

    try {
      selfActionRef.current = true;
      await API.post('/saves', { title: saveTitle, data, color: saveColor, site });
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la sauvegarde (droits ?) RECONNECTE TOI !!!');
      return;
    }

    setSaveTitle('');
    await fetchSaves();

    // On met aussi à jour le state React pour enlever les pastilles
    setColumns((prev) => {
      const next = {};
      containerOrder.forEach((key) => {
        next[key] = {
          ...prev[key],
          items: data[key] || []
        };
      });
      return next;
    });
  };

  const applySave = async (save) => {
    const { data } = await API.get(`/saves/${save.id}`);

    // on repart d'une base propre avec les colonnes initiales
    const baseCols = buildInitialColumns();

    const newCols = containerOrder.reduce((acc, key) => {
      acc[key] = {
        ...baseCols[key], // ✅ nom + éventuelles cases de base
        items: (data[key] || []).map((item) => {
          const bo = String(item.backOffice || '').trim().toUpperCase();

          let cornerTag = item.cornerTag || '';
          if (!cornerTag) {
            if (bo === 'LMFR-SUDO-158-RIVE-STORE' || bo === 'LMFR-SUDO-158-RIVE-SFS') {
              cornerTag = 'R';
            } else if (bo === 'LMFR-SUDO-171-PERP-SFS' || bo === 'LMFR-SUDO-171-PERP-STORE') {
              cornerTag = 'P';
            }
          }

          return {
            ...item,
            cornerTag,
            dateADate: item.dateADate === true || item.dateADate === 1,
            // ✅ si absent (anciennes saves), on met TRUE par défaut sur les departures
            showLoadKg: item.type === 'departure' ? (item.showLoadKg ?? true) : item.showLoadKg,
            isTemporary: false
          };
        })
      };
      return acc;
    }, {});

    setColumns(newCols);
    setActiveSave(save);
  };

  const handleDeleteSave = async (id) => {
    if (!canEdit) {
      alert('Seul un admin peut supprimer une sauvegarde');
      return;
    }
    if (!window.confirm('Supprimer cette sauvegarde ?')) return;
    try {
      selfActionRef.current = true;
      await API.delete(`/saves/${id}`);
      await fetchSaves();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la suppression (droits ?)');
    }
  };

  return {
    saveList,
    saveTitle,
    setSaveTitle,
    saveColor,
    setSaveColor,
    activeSave,
    setActiveSave,
    fetchSaves,
    initPrevSaves,
    handleSave,
    handleDeleteSave,
    applySave,
    persistActiveSaveSilently,
    saveNotifications,
    dismissNotification
  };
}
